<div class="learning-page">
  @if (home(); as item) {
    <section class="learning-hero">
      <div class="learning-hero-copy">
        <span class="kicker">DriveMe Academy</span>
        <h1>Mein Lernbereich</h1>
        <p>
          Willkommen, {{ item.displayName }}. Hier siehst du deine zugewiesenen Module, deinen Fortschritt und deine Teilnahmebestätigungen.
        </p>

        <div class="learning-hero-meta">
          <span>{{ item.totalLessons }} Lektionen</span>
          <span>{{ item.completedLessons }} abgeschlossen</span>
          <span>{{ item.openLessons }} offen</span>
          <span>{{ item.progressPercent }} % Fortschritt</span>
        </div>
      </div>
    </section>

    <section class="warning-panel learning-progress-panel">
      <div>
        <span>Weiter so</span>
        <h2>Du hast bereits {{ item.completedLessons }} Lektionen abgeschlossen.</h2>
        <p>Noch {{ item.openLessons }} Lektionen bis zum nächsten großen Schritt.</p>
      </div>
      <div>
        <strong>{{ item.progressPercent }} %</strong>
        <small>Gesamtfortschritt</small>
      </div>
    </section>

    <section class="learning-layout">
      <div class="panel learning-main-panel">
        <span class="kicker">Meine Schulungen</span>
        <h2 class="mt-2 mb-2 text-3xl font-bold">Zugewiesene Module</h2>
        <p class="mt-0 mb-5 text-app-muted">
          Öffne ein Modul, um die Lektionen einzusehen. Die Lektionen werden in der nummerierten Reihenfolge bearbeitet; nach Video und Zusammenfassung muss der Wissens-Check bestanden werden.
        </p>

        @if (item.enrollments.length) {
          <div class="learning-enrollment-grid">
            @for (enrollment of item.enrollments; track enrollment.id) {
              <article class="learning-enrollment-card" [class.expanded]="enrollmentExpanded(enrollment.id)">
                <div class="learning-enrollment-head">
                  <span class="learning-module-label">Modul {{ enrollment.moduleNumber }}</span>
                  <span class="status-pill" [class.success]="enrollment.status === 'COMPLETED'">
                    {{ statusLabel(enrollment.status) }}
                  </span>
                </div>

                <h3>{{ enrollment.courseTitle }}</h3>
                @if (enrollment.courseDescription) {
                  <p class="learning-module-description">{{ enrollment.courseDescription }}</p>
                }

                <div class="learning-module-meta">
                  <strong>{{ enrollment.totalLessons }} {{ enrollment.totalLessons === 1 ? 'Lektion' : 'Lektionen' }}</strong>
                  <span>{{ enrollment.completedLessons }} erledigt</span>
                  <span>Frist: {{ dueLabel(enrollment.dueDate) }}</span>
                </div>

                <div class="learning-module-progress-head">
                  <strong>Fortschritt</strong>
                  <span>{{ enrollment.progressPercent }} %</span>
                </div>

                <div class="progress-track">
                  <div class="progress-fill" [style.width.%]="enrollment.progressPercent"></div>
                </div>

                <button
                  class="primary-button learning-card-action"
                  type="button"
                  [attr.aria-expanded]="enrollmentExpanded(enrollment.id)"
                  [attr.aria-controls]="'module-details-' + enrollment.id"
                  (click)="toggleEnrollment(enrollment.id)"
                >
                  {{ enrollmentExpanded(enrollment.id) ? 'Modul schließen' : 'Modul öffnen' }}
                  <span aria-hidden="true">{{ enrollmentExpanded(enrollment.id) ? '↑' : '→' }}</span>
                </button>

                @if (enrollmentExpanded(enrollment.id)) {
                  <div class="learning-module-details" [id]="'module-details-' + enrollment.id">
                    <div class="learning-module-details-head">
                      <div>
                        <span class="kicker">Lektionen</span>
                        <h4>Inhalte von Modul {{ enrollment.moduleNumber }}</h4>
                      </div>
                      @if (enrollment.nextLessonId) {
                        <a class="ghost-button as-link" [routerLink]="['/learning', enrollment.id, 'lessons', enrollment.nextLessonId]">
                          Weiterlernen
                        </a>
                      }
                    </div>

                    <div class="learning-lesson-list">
                      @for (lesson of enrollment.lessons; track lesson.id) {
                        <a
                          class="learning-lesson-row"
                          [class.opacity-60]="!lessonAccessible(enrollment, lesson)"
                          [class.pointer-events-none]="!lessonAccessible(enrollment, lesson)"
                          [attr.aria-disabled]="!lessonAccessible(enrollment, lesson)"
                          [routerLink]="lessonAccessible(enrollment, lesson) ? ['/learning', enrollment.id, 'lessons', lesson.id] : null"
                        >
                          <span>
                            <strong>{{ lesson.lessonOrder }}. {{ lesson.title }}</strong>
                            <small>{{ durationLabel(lesson.durationSeconds) }}</small>
                          </span>
                          <span class="status-pill" [class.success]="lesson.completed">
                            {{ lesson.completed ? 'Erledigt' : lessonAccessible(enrollment, lesson) ? 'Offen' : 'Gesperrt' }}
                          </span>
                        </a>
                      }
                    </div>

                    @if (enrollment.finalQuiz && enrollment.finalQuiz.questionCount > 0) {
                      <div class="learning-final-quiz-card">
                        <div>
                          <span class="kicker">Abschließende Prüfung</span>
                          <h4>{{ enrollment.finalQuiz.title }}</h4>
                          <p>{{ enrollment.finalQuiz.questionCount }} Fragen · Bestehen ab {{ enrollment.finalQuiz.passPercentage }} %</p>
                        </div>
                        @if (enrollment.finalQuiz.passed) {
                          <span class="status-pill success">Bestanden · {{ enrollment.finalQuiz.scorePercent }} %</span>
                        } @else if (enrollment.finalQuiz.available) {
                          <a class="primary-button as-link" [routerLink]="['/learning', enrollment.id, 'final-knowledge-check']">Prüfung starten</a>
                        } @else {
                          <span class="status-pill">Nach Lektionen freigeschaltet</span>
                        }
                      </div>
                    }
                  </div>
                }
              </article>
            }
          </div>
        } @else {
          <div class="empty">Dir sind aktuell keine Schulungen zugewiesen.</div>
        }
      </div>
    </section>

    <section class="panel learning-certificate-panel">
      <h2 class="mt-0 text-xl font-bold">Meine Zertifikate / Teilnahmebestätigungen</h2>
      <p class="mb-0 text-app-muted">Hier findest du deine ausgestellten Nachweise.</p>

      @if (item.certificates.length) {
        <div class="learning-certificate-grid">
          @for (certificate of item.certificates; track certificate.id) {
            <article class="learning-certificate-card">
              <div class="learning-certificate-head">
                <span class="badge">Zertifikatsnummer {{ certificate.certificateNumber }}</span>
                <span class="status-pill success">{{ certificate.valid ? 'gültig' : certificateStatusLabel(certificate.status) }}</span>
              </div>

              <h3>{{ certificate.courseTitle }}</h3>
              <p>Ausgestellt am {{ formatDate(certificate.issuedAt) }}</p>
              <p>Gültig bis: {{ certificate.validUntil ? formatDate(certificate.validUntil) : 'ohne Ablaufdatum' }}</p>

              <div class="learning-card-actions">
                <a class="primary-button as-link" [routerLink]="['/certificates', certificate.id]">Details öffnen</a>
              </div>
            </article>
          }
        </div>
      } @else {
        <div class="empty mt-5">Noch keine Zertifikate vorhanden.</div>
      }
    </section>
  } @else {
    <div class="empty">Lernbereich wird geladen.</div>
  }
</div>
